import { State } from '@app/store/state';

import { EmailContentGenerationAttributes, GeneratedContent } from './domain';
import { createDefaultContentGenerationAttributes } from './utils';

export const selectContentRepurposingState = (state: State) =>
  state.contentHub.creatorLab.contentRepurposing;

export const selectToneOfVoiceConfig = (state: State) =>
  state.contentHub.creatorLab.toneOfVoiceConfig;

export const selectEmailAudienceConfig = (state: State) =>
  state.contentHub.creatorLab.emailAudienceConfig;

export const selectCreatorLabSelectedContentType = (state: State) =>
  state.contentHub.creatorLab.selectedContent?.type ?? 'SOCIAL_POST';

export const selectContentGenerationAttributes = (state: State) => {
  const selectedContent = state.contentHub.creatorLab.selectedContent;
  if (selectedContent) {
    return selectedContent.attributes;
  }

  return createDefaultContentGenerationAttributes(
    selectCreatorLabSelectedContentType(state),
  );
};

export const selectCreatorLabSelectedContent = (state: State) =>
  state.contentHub.creatorLab.selectedContent;

export const selectCreatorLabContentHistory = (state: State) =>
  state.contentHub.creatorLab.contentHistory;

export const selectCreatorLabGeneratedContent = (state: State) =>
  state.contentHub.creatorLab.generatedContent;

export const selectCreatorLabIsReady = (state: State): boolean => {
  const contentRepurposing = state.contentHub.creatorLab.contentRepurposing;

  if (
    contentRepurposing.status !== 'success' ||
    !contentRepurposing.data ||
    contentRepurposing.data.eligibility !== 'ELIGIBLE'
  ) {
    return false;
  }

  const { toneOfVoice, topics } = contentRepurposing.data;

  return toneOfVoice?.status === 'FINISHED' && topics.status === 'FINISHED';
};

export const selectCurrentGeneratedContent = (state: State) => {
  const selectedContent = state.contentHub.creatorLab.selectedContent;
  const generatedContent = state.contentHub.creatorLab.generatedContent;

  if (!selectedContent || !selectedContent.id || !generatedContent) {
    return null;
  }

  return generatedContent[selectedContent.id] || null;
};

/**
 * Gets the content generation status (not TransactionState status)
 * from the current generated content
 */
export const selectCurrentContentGenerationStatus = (
  state: State,
): 'initial' | 'in_progress' | 'finished' | 'failed' | null => {
  const content = selectCurrentGeneratedContent(state);
  if (!content) {
    return null;
  }
  if ('content' in content && 'topics' in content) {
    return (content as any).status as
      | 'initial'
      | 'in_progress'
      | 'finished'
      | 'failed';
  }
  return null;
};

/**
 * Checks if content is currently selected (id is not null)
 */
export const selectIsContentSelected = (state: State): boolean => {
  const selectedContent = state.contentHub.creatorLab.selectedContent;
  return selectedContent?.id !== null && selectedContent?.id !== undefined;
};

/**
 * Checks if content generation is in progress (initial or in_progress status)
 */
export const selectIsContentGenerationInProgress = (state: State): boolean => {
  const status = selectCurrentContentGenerationStatus(state);
  return status === 'initial' || status === 'in_progress';
};

/**
 * Selects the isContentLoading flag from state
 * This flag defaults to true to prevent showing empty state initially
 * and is set to false when content is successfully fetched/generated
 *
 * Also returns true if status is null (unknown state) as a safeguard
 */
export const selectIsContentLoading = (state: State): boolean => {
  const isContentLoading = state.contentHub.creatorLab.isContentLoading;
  const status = selectCurrentContentGenerationStatus(state);

  // If status is null (unknown state), default to loading to prevent empty message flicker
  if (status === null) {
    return true;
  }

  return isContentLoading;
};

/**
 * Checks if a content generation request is currently live (API call in progress)
 * or if content generation is in progress based on the latest history entry.
 *
 * Returns true if:
 * - An active API request is in progress (isContentGenerationRequestInProgress), OR
 * - The latest entry in the current content type's history has status 'initial' or 'in_progress'
 *
 * This prevents 409 conflicts by disabling the regenerate button when content
 * generation is already in progress for the current content type.
 */
export const selectIsContentGenerationRequestLive = (state: State): boolean => {
  // Check if an active API request is in progress
  if (state.contentHub.creatorLab.isContentGenerationRequestInProgress) {
    return true;
  }

  // Check the latest entry in the current content type's history
  const contentType = selectCreatorLabSelectedContentType(state);
  const contentHistory =
    state.contentHub.creatorLab.contentHistory[contentType];

  // If history is not loaded or has no entries, allow regeneration
  if (
    !contentHistory ||
    contentHistory.status !== 'success' ||
    !contentHistory.history ||
    contentHistory.history.length === 0
  ) {
    return false;
  }

  // Get the latest entry (first entry in the array)
  const latestEntry = contentHistory.history[0];
  const latestStatus = latestEntry?.status;

  // Disable if latest entry is in progress or initial state
  return latestStatus === 'initial' || latestStatus === 'in_progress';
};

/**
 * Checks if attribute selectors should be disabled
 * Disabled only when: content generation request is live
 */
export const selectShouldDisableAttributeSelectors = (
  state: State,
): boolean => {
  return selectIsContentGenerationRequestLive(state);
};

/**
 * Checks if content history fetch failed for the current content type
 */
export const selectIsContentHistoryFetchFailed = (state: State): boolean => {
  const contentType = selectCreatorLabSelectedContentType(state);
  const contentHistory =
    state.contentHub.creatorLab.contentHistory[contentType];
  return contentHistory?.status === 'failed';
};

/**
 * Checks if any content generation attributes have changed from the currently selected content
 * Returns true if current attributes differ from the attributes used to generate the selected content
 */
export const selectHaveContentGenerationAttributesChanged = (
  state: State,
): boolean => {
  const selectedContent = state.contentHub.creatorLab.selectedContent;
  const currentContent = selectCurrentGeneratedContent(state);

  if (!selectedContent?.id || !currentContent) {
    return false;
  }

  if (
    !('topics' in currentContent) ||
    !('toneVoiceId' in currentContent) ||
    !('status' in currentContent)
  ) {
    return false;
  }

  const generationStatus = (currentContent as GeneratedContent).status;
  if (generationStatus === 'initial' || generationStatus === 'in_progress') {
    return false;
  }

  const { toneVoiceId, topics, context } = currentContent;
  const { attributes, type: contentType } = selectedContent;

  if (attributes.toneOfVoiceId !== toneVoiceId) {
    return true;
  }

  if (contentType === 'SOCIAL_POST' || contentType === 'BLOG_POST') {
    const currentTopicId = attributes.topicId;
    const contentTopicId = topics[0] ?? null;

    if (currentTopicId !== contentTopicId) {
      return true;
    }
  }

  if (contentType === 'EMAIL') {
    const emailAttributes = attributes as EmailContentGenerationAttributes;
    const currentAudienceId = emailAttributes.emailAudienceId;
    const currentTypeId = emailAttributes.emailTypeId;
    const contentAudienceId = context?.recipientTypeId ?? null;
    const contentTypeId = context?.emailTypeId ?? null;

    if (
      currentAudienceId !== contentAudienceId ||
      currentTypeId !== contentTypeId
    ) {
      return true;
    }
  }

  return false;
};
